import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import type { RdoStatus } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { dateOnlyToDate, dateToDateOnly } from "@/lib/format";
import {
	RDOS_PAGE_SIZE,
	type RdosFilters,
	useRdos,
} from "../../hooks/use-rdos.ts";
import {
	formatRdoDate,
	formatRdoDateTime,
	formatRdoRevision,
} from "../../utils/format.ts";
import { RDO_STATUS, RDO_STATUSES } from "../../utils/status.ts";
import { RdoStatusBadge } from "../rdo-status-badge";

const ALL_STATUSES = "todos";

function hasActiveFilters(filters: RdosFilters) {
	return !!(
		filters.from ||
		filters.to ||
		filters.status ||
		filters.includeSuperseded
	);
}

export function RdoListSkeleton() {
	return (
		<Card className="rdo-list__card">
			<div className="rdo-list__skeleton">
				{Array.from({ length: 4 }, (_, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
					<Skeleton key={index} className="rdo-list__skeleton-row" />
				))}
			</div>
		</Card>
	);
}

export function RdoList({ constructionId }: { constructionId: string }) {
	const { obra: obraKey } = useParams();
	const [filters, setFilters] = useState<RdosFilters>({ page: 1 });
	const query = useRdos(constructionId, filters);

	const fromDate = dateOnlyToDate(filters.from);
	const toDate = dateOnlyToDate(filters.to);
	const page = filters.page ?? 1;
	const count = query.data?.count ?? 0;
	const pageCount = Math.max(1, Math.ceil(count / RDOS_PAGE_SIZE));

	function patch(partial: Partial<RdosFilters>) {
		setFilters((current) => ({ ...current, ...partial, page: 1 }));
	}

	function body() {
		if (query.isPending) return <RdoListSkeleton />;
		if (query.isError) {
			return (
				<Card>
					<div className="card-state">
						<p>Não foi possível carregar os Relatórios de Obra.</p>
						<Button variant="outline" onClick={() => query.refetch()}>
							Tentar novamente
						</Button>
					</div>
				</Card>
			);
		}
		const items = query.data.items;
		return (
			<Card className="rdo-list__card">
				<Table>
					<TableHeader className="rdo-list__thead">
						<TableRow>
							<TableHead className="rdo-list__col-number">Nº</TableHead>
							<TableHead>Data</TableHead>
							<TableHead>Revisão</TableHead>
							<TableHead>Status</TableHead>
							<TableHead className="rdo-list__col-updated">
								Atualizado
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{items.length === 0 ? (
							<TableRow>
								<TableCell colSpan={5} className="rdo-list__empty">
									{hasActiveFilters(filters)
										? "Nenhum Relatório de Obra para este filtro."
										: "Nenhum Relatório de Obra criado ainda."}
								</TableCell>
							</TableRow>
						) : (
							items.map((rdo) => (
								<TableRow key={rdo.id} data-status={rdo.status}>
									<TableCell className="rdo-list__number-cell">
										<Link
											to={`/${obraKey}/rdo/${rdo.id}`}
											className="rdo-list__link"
										>
											Relatório de Obra nº {rdo.sequenceNumber}
										</Link>
									</TableCell>
									<TableCell>{formatRdoDate(rdo.reportDate)}</TableCell>
									<TableCell>{formatRdoRevision(rdo.revision)}</TableCell>
									<TableCell>
										<RdoStatusBadge status={rdo.status} />
									</TableCell>
									<TableCell className="rdo-list__updated-cell">
										{formatRdoDateTime(rdo.updatedAt)}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
				{pageCount > 1 && (
					<div className="rdo-list__pagination">
						<span className="rdo-list__pagination-label">
							Página {page} de {pageCount}
						</span>
						<div className="rdo-list__pagination-actions">
							<Button
								variant="outline"
								size="sm"
								disabled={page <= 1}
								onClick={() =>
									setFilters((current) => ({ ...current, page: page - 1 }))
								}
							>
								<ChevronLeft /> Anterior
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={page >= pageCount}
								onClick={() =>
									setFilters((current) => ({ ...current, page: page + 1 }))
								}
							>
								Próxima <ChevronRight />
							</Button>
						</div>
					</div>
				)}
			</Card>
		);
	}

	return (
		<div className="rdo-list">
			<div className="rdo-list__filters">
				<div className="rdo-list__filter">
					<span className="rdo-list__filter-label">De</span>
					<DatePicker
						aria-label="De"
						selected={fromDate}
						onSelected={(next) =>
							patch({ from: next ? dateToDateOnly(next) : undefined })
						}
						calendarProps={toDate ? { disabled: { after: toDate } } : undefined}
					/>
				</div>
				<div className="rdo-list__filter">
					<span className="rdo-list__filter-label">Até</span>
					<DatePicker
						aria-label="Até"
						selected={toDate}
						onSelected={(next) =>
							patch({ to: next ? dateToDateOnly(next) : undefined })
						}
						calendarProps={
							fromDate ? { disabled: { before: fromDate } } : undefined
						}
					/>
				</div>
				<div className="rdo-list__filter rdo-list__filter--status">
					<span className="rdo-list__filter-label">Status</span>
					<Select
						value={filters.status ?? ALL_STATUSES}
						onValueChange={(next) =>
							patch({
								status: next === ALL_STATUSES ? undefined : (next as RdoStatus),
							})
						}
					>
						<SelectTrigger
							aria-label="Status"
							className="rdo-list__status-trigger"
						>
							<SelectValue placeholder="Todos os status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL_STATUSES}>Todos os status</SelectItem>
							{RDO_STATUSES.map((status) => (
								<SelectItem key={status} value={status}>
									{RDO_STATUS[status].label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="rdo-list__filter rdo-list__filter--switch">
					<Switch
						id="rdo-list-superseded"
						checked={!!filters.includeSuperseded}
						onCheckedChange={(checked) =>
							patch({ includeSuperseded: checked || undefined })
						}
					/>
					<Label htmlFor="rdo-list-superseded">Mostrar substituídos</Label>
				</div>
				{hasActiveFilters(filters) && (
					<Button
						variant="ghost"
						size="sm"
						className="rdo-list__clear"
						onClick={() => setFilters({ page: 1 })}
					>
						<X /> Limpar filtros
					</Button>
				)}
			</div>

			{body()}
		</div>
	);
}
